import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { getScanLogs } from "../../../../api/credentialScanning";

const LEVEL_STYLE = {
  INFO:    { color: "#90caf9", label: "INFO   " },
  WARNING: { color: "#ffcc02", label: "WARN   " },
  ERROR:   { color: "#ef9a9a", label: "ERROR  " },
  FINDING: { color: "#a5d6a7", label: "FINDING" },
};

const TERMINAL_BG = "#0d1117";
const TERMINAL_TEXT = "#c9d1d9";

/**
 * Live scan log panel.
 *
 * Props:
 *   scan        — the scan row object { id, status }
 *   onClose     — called when the panel should close
 */
export default function ScanLogsPanel({ scan, onClose }) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [scanStatus, setScanStatus] = useState(scan.status);
  const [error, setError] = useState(null);
  const sinceIdRef = useRef(0);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const fetchLogs = useCallback(async () => {
    const { data, error: err } = await getScanLogs(scan.id, sinceIdRef.current);
    if (err) {
      setError(t("credentialScanning.toastFailedToFetchLogs"));
      return;
    }
    if (data) {
      setScanStatus(data.scan_status);
      if (data.logs && data.logs.length > 0) {
        setLogs((prev) => [...prev, ...data.logs]);
        sinceIdRef.current = data.logs[data.logs.length - 1].id;
      }
    }
  }, [scan.id]);

  // Initial fetch + poll while running
  useEffect(() => {
    fetchLogs();
    const isActive = ["PENDING", "IN_PROGRESS"].includes(scanStatus);
    if (isActive) {
      pollRef.current = setInterval(fetchLogs, 2000);
    }
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop polling once scan is no longer active
  useEffect(() => {
    if (!["PENDING", "IN_PROGRESS"].includes(scanStatus)) {
      clearInterval(pollRef.current);
      // One final fetch to capture the completion log
      fetchLogs();
    }
  }, [scanStatus, fetchLogs]);

  // Auto-scroll to bottom on new logs
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs]);

  const isLive = ["PENDING", "IN_PROGRESS"].includes(scanStatus);

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          width: "min(900px, 95vw)",
          height: "min(600px, 85vh)",
          background: TERMINAL_BG,
          borderRadius: 8,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
          overflow: "hidden",
        }}
      >
        {/* Title bar */}
        <div
          style={{
            padding: "10px 16px",
            background: "#161b22",
            borderBottom: "1px solid #30363d",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: "#58a6ff", fontWeight: 700, fontFamily: "monospace", fontSize: 13 }}>
              {t("credentialScanning.scanLogsTitle", { id: scan.id })}
            </span>
            {isLive ? (
              <span style={{
                background: "#1f6feb",
                color: "#fff",
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 7px",
                borderRadius: 10,
                letterSpacing: 0.5,
              }}>
                ● {t("credentialScanning.logsLive")}
              </span>
            ) : (
              <span style={{
                background: "#21262d",
                color: "#8b949e",
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 7px",
                borderRadius: 10,
                letterSpacing: 0.5,
              }}>
                {scanStatus}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#8b949e",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              padding: "0 4px",
            }}
            title={t("credentialScanning.btnClose")}
          >
            ✕
          </button>
        </div>

        {/* Log body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px 16px",
            fontFamily: "'Courier New', 'Consolas', monospace",
            fontSize: 12,
            lineHeight: "1.7",
            color: TERMINAL_TEXT,
          }}
        >
          {error && (
            <div style={{ color: "#ef9a9a", marginBottom: 8 }}>{error}</div>
          )}

          {logs.length === 0 && !error && (
            <div style={{ color: "#484f58", fontStyle: "italic" }}>
              {isLive ? t("credentialScanning.logsWaitingEvents") : t("credentialScanning.logsNoEntries")}
            </div>
          )}

          {logs.map((log) => {
            const style = LEVEL_STYLE[log.level] || LEVEL_STYLE.INFO;
            return (
              <div key={log.id} style={{ display: "flex", gap: 10, marginBottom: 1 }}>
                <span style={{ color: "#484f58", flexShrink: 0, userSelect: "none" }}>
                  {new Date(log.timestamp).toLocaleTimeString([], {
                    hour: "2-digit", minute: "2-digit", second: "2-digit",
                  })}
                </span>
                <span style={{ color: style.color, flexShrink: 0, fontWeight: 700, userSelect: "none" }}>
                  {style.label}
                </span>
                <span style={{
                  color: log.level === "FINDING" ? "#a5d6a7" :
                         log.level === "ERROR"   ? "#ef9a9a" :
                         log.level === "WARNING" ? "#ffd54f" :
                         TERMINAL_TEXT,
                  wordBreak: "break-all",
                }}>
                  {log.message}
                </span>
              </div>
            );
          })}

          {isLive && (
            <div style={{ color: "#484f58", fontStyle: "italic", marginTop: 4 }}>
              ▋ {t("credentialScanning.logsScanningDots")}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "8px 16px",
            background: "#161b22",
            borderTop: "1px solid #30363d",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <span style={{ color: "#484f58", fontSize: 11, fontFamily: "monospace" }}>
            {t("credentialScanning.logsEntryCount", { count: logs.length })}
          </span>
          <button
            onClick={onClose}
            style={{
              padding: "4px 14px",
              background: "#21262d",
              color: "#c9d1d9",
              border: "1px solid #30363d",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            {t("credentialScanning.btnClose")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
